import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { usePatientFormContext } from "../../contexts/PatientForm";

import {
	findPatientForm,
	findPatientFormProtected,
} from "../../processes/patient-form";

import PrivacyPolicy from "./PrivacyPolicy";
import * as NavigationUtils from "../../utils/navigation";
import { formatCpf } from "../../utils/formatter";
import PassCodeContainer from "../../components/PassCodeContainer";
import Header from "../../components/Header";
import Container from '../../components/Container';
import useTermsModal from "../../hooks/useTermsModal";


function PrivacyPolicyContainer() {
	const { setInfo } = usePatientFormContext();

	const [acceptedTerms, setAcceptedTerms] = useState(false);

	const history = useHistory();
	const { code } = useParams();

	const [cpf, setCpf] = useState("");
	const [loading, setLoading] = useState(false);
	const [form, setForm] = useState({});
	const { isOpen, loading: loadingModal, content, title, closeModal, openTerms, openPrivacy } = useTermsModal();

	const fetchPatientForm = useCallback(async () => {
		const filter = {
			code,
		};

		setLoading(true);

		const { data, error } = await findPatientForm(filter);

		if (error) {
			if (error.code === "INVALID_CODE") {
				return NavigationUtils.navigate(
					history,
					`/expired/${code || ""}`
				);
			} else {
				toast.error(
					"Não foi possível buscar as informações do formulário."
				);
			}
		} else {
			setForm(data);

			setInfo({
				form: data,
				code,
			});
		}

		setLoading(false);

		if (data?.status === "ANSWERED") {
			return NavigationUtils.navigate(
				history,
				`/forms/signature-success/${code}`
			);
		}
	}, [code, history, setInfo]);

	useEffect(() => {
		fetchPatientForm();
	}, [fetchPatientForm]);

	const navigateToCurrentStep = useCallback(() => {
		const url =
			form?.status === "ANSWERED"
				? `/forms/signature-success/${code}`
				: `/forms/${code}`;

		history.push(url);
	}, [history, form?.status, code]);

	const handleValidateFormProtectionAccess = useCallback(
		async (passCode) => {
			const filter = {
				code,
				document_number: passCode,
			};

			setLoading(true);

			const { data, error } = await findPatientFormProtected(filter);

			setLoading(false);

			if (error) {
				toast.error("Código inválido!");
			} else {
				setForm(data);
				setInfo({
					form: {
						...data,
						request_pass_code: false,
						pass_code: passCode,
					},
					code,
				});
			}
		},
		[code, setInfo]
	);

	const onChangeCpfText = useCallback(({ target }) => {
		const maskedValue = formatCpf(target?.value);

		setCpf(maskedValue);
	}, []);

	const handleSubmit = useCallback(() => {
		navigateToCurrentStep();
	}, [navigateToCurrentStep]);

	return (
		<>
			{form.request_pass_code ? (
				<Container>
					<Header />

					<PassCodeContainer
						onSubmit={handleValidateFormProtectionAccess}
						loading={loading}
					/>
				</Container>
			) : (
				<PrivacyPolicy
					cpf={cpf}
					form={form}
					setCpf={setCpf}
					loading={loading}
					onSubmit={handleSubmit}
					acceptedTerms={acceptedTerms}
					onChangeCpfText={onChangeCpfText}
					setAcceptedTerms={setAcceptedTerms}
					isOpen={isOpen}
					loadingModal={loadingModal}
					content={content}
					title={title}
					closeModal={closeModal}
					openTerms={openTerms}
					openPrivacy={openPrivacy}
				/>
			)}
		</>
	);
}

export default PrivacyPolicyContainer;
